import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../reporting/ReportingShared";
import { getPosLoyaltyReturns, fetchBranches } from "../../utils/apiCalls";

const inputStyle = {
    padding: "10px 14px", borderRadius: 10, fontSize: 13,
    border: `1px solid ${C.border}`, background: C.white,
    color: C.text, outline: "none", minWidth: 160,
};

const PosLoyaltyReturns = () => {
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((s) => s.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [isLoading, setIsLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [branches, setBranches] = useState([]);
    const [expanded, setExpanded] = useState(null);

    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);

    const [filters, setFilters] = useState({
        branchId: "",
        startDate: "",
        endDate: "",
        search: "",
    });

    useEffect(() => {
        if (!selectedMerchant || selectedMerchant === "all") {
            setBranches([]);
            return;
        }
        (async () => {
            const res = await fetchBranches({ MerchantId: selectedMerchant });
            setBranches(res?.data || []);
        })();
    }, [selectedMerchant]);

    const fetchData = useCallback(async () => {
        if (!selectedMerchant || selectedMerchant === "all") {
            setRows([]); setTotalPages(1); return;
        }
        setIsLoading(true);
        const res = await getPosLoyaltyReturns(page, limit, { ...filters, merchantId: selectedMerchant });
        if (res) {
            setRows(res.rows || []);
            setTotalPages(res.pagination?.totalPages || 1);
        } else {
            setRows([]); setTotalPages(1);
        }
        setIsLoading(false);
    }, [page, limit, filters, selectedMerchant]);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { setPage(1); }, [filters, selectedMerchant]);

    const updateFilter = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
    const clearFilters = () => setFilters({ branchId: "", startDate: "", endDate: "", search: "" });

    const pageBtnStyle = (active, disabled) => ({
        minWidth: 36, height: 36, padding: "0 12px", borderRadius: 8,
        border: `1px solid ${active ? C.coral : C.border}`,
        background: active ? `linear-gradient(135deg, ${C.coral}, #00bfa5)` : C.white,
        color: active ? C.white : C.textMed,
        fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
    });

    const pageNumbers = useMemo(() => {
        const max = 5;
        const start = Math.max(1, page - Math.floor(max / 2));
        const end = Math.min(totalPages, start + max - 1);
        const arr = [];
        for (let i = start; i <= end; i++) arr.push(i);
        return arr;
    }, [page, totalPages]);

    return (
        <Fragment>
            <Breadcrumb title="POS Loyalty Returns" parent="Loyalty" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">
                    <RCard title="Filters">
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
                            <div>
                                <div style={{ fontSize: 12, color: C.textMed, marginBottom: 6 }}>Branch</div>
                                <select
                                    style={inputStyle}
                                    value={filters.branchId}
                                    onChange={(e) => updateFilter("branchId", e.target.value)}
                                >
                                    <option value="">All branches</option>
                                    {branches.map((b) => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <div style={{ fontSize: 12, color: C.textMed, marginBottom: 6 }}>From</div>
                                <input
                                    type="datetime-local"
                                    style={inputStyle}
                                    value={filters.startDate}
                                    onChange={(e) => updateFilter("startDate", e.target.value)}
                                />
                            </div>
                            <div>
                                <div style={{ fontSize: 12, color: C.textMed, marginBottom: 6 }}>To</div>
                                <input
                                    type="datetime-local"
                                    style={inputStyle}
                                    value={filters.endDate}
                                    onChange={(e) => updateFilter("endDate", e.target.value)}
                                />
                            </div>
                            <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ fontSize: 12, color: C.textMed, marginBottom: 6 }}>Search (Return ID / Order Ext. ID)</div>
                                <input
                                    type="text"
                                    style={{ ...inputStyle, width: "100%" }}
                                    placeholder="e.g. POS-RET-1 or POS-SALE-12345"
                                    value={filters.search}
                                    onChange={(e) => updateFilter("search", e.target.value)}
                                />
                            </div>
                            <button
                                style={{ ...inputStyle, cursor: "pointer", background: C.white, fontWeight: 600 }}
                                onClick={clearFilters}
                            >
                                Clear
                            </button>
                        </div>
                    </RCard>

                    <RCard title={`Returns (${rows.length})`}>
                        {isLoading ? (
                            <Loader />
                        ) : rows.length === 0 ? (
                            <div style={{ padding: 24, textAlign: "center", color: C.textMed }}>No returns found</div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr style={{ background: C.borderLight, textAlign: "left" }}>
                                            <th style={th}></th>
                                            <th style={th}>Date</th>
                                            <th style={th}>Return ID</th>
                                            <th style={th}>Order Ext. ID</th>
                                            <th style={th}>Branch</th>
                                            <th style={th}>Cashbox</th>
                                            <th style={th}>Customer</th>
                                            <th style={th}>Type</th>
                                            <th style={th}>Stamps Revoked</th>
                                            <th style={th}>Rewards Revoked</th>
                                            <th style={th}>Rewards Restored</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const isOpen = expanded === r.id;
                                            const customer = r.Order?.User
                                                ? `${r.Order.User.firstName || ""} ${r.Order.User.lastName || ""}`.trim() || r.Order.User.phoneNo
                                                : "—";
                                            return (
                                                <Fragment key={r.id}>
                                                    <tr
                                                        style={{ borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}
                                                        onClick={() => setExpanded(isOpen ? null : r.id)}
                                                    >
                                                        <td style={td}>{isOpen ? "▾" : "▸"}</td>
                                                        <td style={td}>{new Date(r.createdAt).toLocaleString()}</td>
                                                        <td style={td}>{r.externalReturnId}</td>
                                                        <td style={td}>{r.Order?.externalId || "—"}</td>
                                                        <td style={td}>{r.Order?.Branch?.name || "—"}</td>
                                                        <td style={td}>{r.cashboxId || "—"}</td>
                                                        <td style={td}>{customer}</td>
                                                        <td style={td}>
                                                            {r.isFullReturn
                                                                ? <Badge color={C.coral} bg={C.coralLight ?? "#e0f2f1"}>Full</Badge>
                                                                : <Badge color={C.orange} bg={C.orangeLight}>Partial</Badge>}
                                                        </td>
                                                        <td style={td}>{r.stampsRevoked}</td>
                                                        <td style={td}>{r.rewardsRevoked}</td>
                                                        <td style={td}>{r.rewardsRestored}</td>
                                                    </tr>
                                                    {isOpen && (
                                                        <tr style={{ background: C.borderLight }}>
                                                            <td colSpan={11} style={{ padding: 16 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Returned Items</div>
                                                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={th}>Product</th>
                                                                            <th style={th}>Merchant Product ID</th>
                                                                            <th style={th}>Qty Returned</th>
                                                                            <th style={th}>Original Line Qty</th>
                                                                            <th style={th}>Line Price</th>
                                                                            <th style={th}>Reward Item?</th>
                                                                            <th style={th}>Reward Restored?</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(r.OrderReturnItems || []).map((it) => (
                                                                            <tr key={it.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                                                                <td style={td}>{it.OrderItem?.Product?.title || "—"}</td>
                                                                                <td style={td}>{it.OrderItem?.Product?.merchant_product_id || "—"}</td>
                                                                                <td style={td}>{it.quantity}</td>
                                                                                <td style={td}>{it.OrderItem?.quantity ?? "—"}</td>
                                                                                <td style={td}>{it.OrderItem?.price ?? "—"}</td>
                                                                                <td style={td}>{it.OrderItem?.isRewardRedemption ? "Yes" : "No"}</td>
                                                                                <td style={td}>{it.rewardRestored ? "Yes" : "No"}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16 }}>
                                <button style={pageBtnStyle(false, page === 1)} disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                                {pageNumbers.map((n) => (
                                    <button key={n} style={pageBtnStyle(n === page, false)} onClick={() => setPage(n)}>{n}</button>
                                ))}
                                <button style={pageBtnStyle(false, page === totalPages)} disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                            </div>
                        )}
                    </RCard>
                </div>
            </Container>
        </Fragment>
    );
};

const th = { padding: "10px 12px", fontSize: 12, color: C.textMed, fontWeight: 600 };
const td = { padding: "10px 12px", fontSize: 13, color: C.text };

export default PosLoyaltyReturns;
